import React, { useEffect, useRef, Suspense } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { usePageViews } from '@whitelabel/helpers/hooks';
import { TEST_CHAT_CUSTOMER_EMAILS } from '@whitelabel/xcover-shared/helpers/constants';
import * as actions from '../../actions';
import { ICustomer, IUser } from '../../helpers/types';
import { authRoutes } from '../../routes/routeHelpers';
import { routes, unauthRoutes, componentsMap } from '../../routes/routes';
import AuthRoute from '../helpers/AuthRoute';
import UnauthRoute from '../helpers/UnauthRoute';
import StyledLoading from '../../styled/StyledLoading';
import ConsentBanner from '../../components/ConsentBanner';
import ErrorBoundary from '../ErrorBoundary';
import ErrorPage from '../../components/ErrorPage';
import { storeINSInLocalStorage } from '../../helpers/bookings';

interface IAppProps {
  authenticating: boolean;
  checkSession: (...args: any[]) => any;
  customer?: ICustomer;
  user?: IUser;
}

const App = ({ checkSession, authenticating, customer, user }: IAppProps) => {
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);

  usePageViews();

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    storeINSInLocalStorage();
    if (pathRef.current !== pathname) {
      window.scroll({ top: 0, left: 0 });
      pathRef.current = pathname;
    }
  }, [pathname]);

  useEffect(() => {
    if (window.zE) {
      if (user && customer && TEST_CHAT_CUSTOMER_EMAILS.includes(customer.email ?? '')) {
        window.zE('webWidget', 'identify', {
          email: customer.email,
        });
        window.zE('webWidget', 'show');
      } else {
        window.zE('webWidget', 'hide');
      }
    }
  }, [user, customer]);

  return authenticating ? (
    <StyledLoading />
  ) : (
    <>
      <Suspense fallback={<StyledLoading />}>
        <Switch>
          <Redirect
            from="/(.+)/"
            strict
            exact
            to={{ pathname: pathname.slice(0, -1), search: window.location.search }}
          />
          {unauthRoutes.map((route) => (
            // eslint-disable-next-line react/jsx-key
            <UnauthRoute {...route} />
          ))}

          {authRoutes.map((route) => (
            // eslint-disable-next-line react/jsx-key
            <AuthRoute {...route} component={componentsMap[route.key as keyof typeof componentsMap]} />
          ))}

          {routes.map(({ component: Component, ...restProps }) => (
            // eslint-disable-next-line react/jsx-key
            <Route
              {...restProps}
              render={(routeProps) => (
                <ErrorBoundary>
                  <Component {...routeProps} />
                </ErrorBoundary>
              )}
            />
          ))}
          <Route path="/" exact render={() => <StyledLoading />} />
          <Route render={(routerProps) => <ErrorPage {...routerProps} isNoMatch />} />
        </Switch>
      </Suspense>
      {(!customer || !customer.gdprConsent) && !window.location.pathname.includes('/invoice/') && (
        <ConsentBanner customer={customer} />
      )}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  authenticating: state.user.checkingSession,
  user: state.user.data,
  customer: state.customer.data,
});

const mapDispatchToProps = {
  checkSession: actions.checkSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
